import { Controller } from "@hotwired/stimulus";

// Connects with data-controller="test-engine-filters"
export default class extends Controller<HTMLElement> {
  static targets = ["tagDialog", "tagDialogKey", "tagDialogValue", "tagDialogFilterMethod"];
  declare tagDialogTarget: HTMLDialogElement;
  declare tagDialogKeyTarget: HTMLInputElement;
  declare tagDialogValueTarget: HTMLInputElement;
  declare tagDialogFilterMethodTarget: HTMLSelectElement;

  // Set filter in the URL params and visit the new URL.
  // This action is triggered when the filter option is clicked, except for tags.
  setFilter(event: Event) {
    event.preventDefault();

    const target = event.currentTarget as HTMLElement;
    const name = target.getAttribute("data-name") as string;
    const value = target.getAttribute("data-value") as string;

    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);

    switch (name) {
      case "labels":
      case "owners":
        this.setArrayParam(params, name, value);
        break;
      case "result":
        this.setTagParam(params, "result", value);
        break;
      default:
        if (value === "") {
          params.delete(name);
        } else {
          params.set(name, value);
        }
        break;
    }

    currentUrl.search = params.toString();
    Turbo.visit(currentUrl.toString());
  }

  // Set tag filter in the URL params and visit the new URL.
  // This action is triggered when the tag dialog form is submitted.
  setTagFilter(event: Event) {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;
    const filterMethod = form.filter_method.value;
    const tagValue = form.tag_value.value;
    const tagKey = form.tag_key.value;

    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);

    const value = filterMethod === "equals" ? tagValue : `${tagValue}*`;

    this.setTagParam(params, tagKey, value);

    currentUrl.search = params.toString();
    Turbo.visit(currentUrl.toString());
  }

  // Open the tag dialog for a specific tag and set the key and value for the form in the dialog.
  // This action is trigger when the tag option is clicked.
  openTagDialog(event: Event) {
    const target = event.currentTarget as HTMLElement;
    const key = target.getAttribute("data-tag-key") as string;
    const value = target.getAttribute("data-value") as string;
    const filterMethod = target.getAttribute("data-filter-method") as string;

    // Set the tag key and value for the form in the dialog
    this.tagDialogKeyTarget.value = key;
    this.tagDialogFilterMethodTarget.value = filterMethod;
    this.tagDialogValueTarget.value = value.replace("*", "");

    this.tagDialogTarget.showModal();
  }

  // Hide options that do not match the search input.
  // If there are no matching options, show the empty state.
  searchFilterOptions(event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    const search = target.value;
    const groupName = target.name;

    const options = document
      .querySelector(`[data-filter-group="${groupName}"]`)
      ?.querySelectorAll("li");

    let filteredOptionsCount = 0;

    options?.forEach((option) => {
      if (option.textContent?.includes(search)) {
        option.classList.remove("hidden");
        filteredOptionsCount++;
      } else {
        option.classList.add("hidden");
      }
    });

    // Show/hide empty state
    const emptyState = document
      .querySelector(`[data-filter-group="${groupName}"]`)
      ?.querySelector("[data-empty-state]");
    if (filteredOptionsCount === 0) {
      emptyState?.classList.remove("hidden");
    } else {
      emptyState?.classList.add("hidden");
    }
  }

  closeTagDialog() {
    this.tagDialogTarget.close();

    // Reset the tag key and value for the dialog
    this.tagDialogKeyTarget.value = "";
    this.tagDialogFilterMethodTarget.value = "";
    this.tagDialogValueTarget.value = "";
  }

  private setArrayParam(params: URLSearchParams, name: string, value: string) {
    const filterParam = params.get(name) || "";
    const filters = new Set<string>();

    filterParam
      .split(",")
      .filter(Boolean)
      .forEach((filter) => {
        filters.add(filter);
      });

    if (filters.has(value)) {
      filters.delete(value);
    } else {
      filters.add(value);
    }

    if (filters.size === 0) {
      params.delete(name);
    } else {
      params.set(name, [...filters].join(","));
    }
  }

  private setTagParam(params: URLSearchParams, key: string, value: string) {
    const tagsParam = params.get("tags") || "";
    const tags = new Map<string, string>();

    tagsParam
      .split(",")
      .filter(Boolean)
      .forEach((tag) => {
        const [key, value] = tag.split(":");
        tags.set(key, value);
      });

    if (value) {
      tags.set(key, value);
    } else {
      tags.delete(key);
    }

    if (tags.size === 0) {
      params.delete("tags");
    } else {
      params.set("tags", [...tags].map(([key, value]) => `${key}:${value}`).join(","));
    }
  }
}
